import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { CategoryTree } from "@/components/agent/CategoryTree";
import { KbCategoriesPanel } from "@/components/agent/KbCategoriesPanel";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Tabs, type TabItem } from "@/components/ui/Tabs";
import { SelectMenu, type SelectMenuItem } from "@/components/ui/SelectMenu";
import { ChevronDownIcon } from "@/components/ui/icons";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";

const STATE_FILTERS = ["all", "draft", "review", "published", "archived"] as const;
type StateFilter = (typeof STATE_FILTERS)[number];

export type KbTab = "articles" | "categories";

const STATE_TONE: Record<string, "muted" | "accent" | "success" | "warn"> = {
  draft: "muted",
  review: "warn",
  published: "success",
  archived: "muted",
};

export type KbSearch = {
  category_id?: number;
  state?: StateFilter;
  tag?: string;
  /** Which top-level tab is active; "articles" is the default (omitted). */
  tab?: KbTab;
  /** Deep-link: open the create-category drawer on the categories tab. */
  new?: boolean;
};

/** How many tag pills the filter bar shows before collapsing behind "more". */
const TAG_BAR_LIMIT = 12;

export function KbPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate({ from: "/agent/kb" });
  const search = useSearch({ from: "/agent/kb" }) as KbSearch;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const tab: KbTab = search.tab ?? "articles";
  const categoryId = search.category_id ?? null;
  const state = search.state ?? "all";
  const activeTags = useMemo(
    () => (search.tag ?? "").split(",").map((s) => s.trim()).filter(Boolean),
    [search.tag],
  );
  const [tagsExpanded, setTagsExpanded] = useState(false);

  const setSearch = (patch: Partial<KbSearch>) => {
    void navigate({
      search: (prev: KbSearch) => ({ ...prev, ...patch }),
      replace: true,
    });
  };

  const tabItems: TabItem[] = [
    { id: "articles", label: t("kb.tabArticles") },
    { id: "categories", label: t("kb.tabCategories") },
  ];

  const openCategoriesCreate = () => setSearch({ tab: "categories", new: true });

  // The `new` deep-link is consumed once by the categories panel on mount; the
  // panel's mount effect runs before this one, so stripping `new` here doesn't
  // close the just-opened drawer, but does stop a later remount from reopening it.
  useEffect(() => {
    if (search.new) setSearch({ new: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.new]);

  const categoriesQ = useQuery({
    queryKey: ["kb", "categories"],
    queryFn: () => api.listKbCategories(),
  });

  // Unfiltered fetch to derive per-category counts for the sidebar tree.
  const allArticlesQ = useQuery({
    queryKey: ["kb", "articles", "all"],
    queryFn: () => api.listKbArticles(),
  });

  const articlesQ = useQuery({
    queryKey: ["kb", "articles", { categoryId, state, tag: search.tag ?? null }],
    queryFn: () =>
      api.listKbArticles({
        category_id: categoryId ?? undefined,
        state: state === "all" ? undefined : state,
        tag: activeTags.length > 0 ? activeTags.join(",") : undefined,
      }),
  });

  const tagsQ = useQuery({ queryKey: ["kb", "tags"], queryFn: () => api.listKbTags() });

  // Filter bar shows the most-used tags first; active ones are always kept
  // visible even when they fall outside the top slice.
  const tagBar = useMemo(() => {
    const sorted = [...(tagsQ.data ?? [])].sort((a, b) => b.article_count - a.article_count);
    const visible = tagsExpanded ? sorted : sorted.slice(0, TAG_BAR_LIMIT);
    for (const name of activeTags) {
      if (!visible.some((tg) => tg.name === name)) {
        const full = sorted.find((tg) => tg.name === name);
        if (full) visible.push(full);
      }
    }
    return { visible, hasMore: sorted.length > TAG_BAR_LIMIT };
  }, [tagsQ.data, tagsExpanded, activeTags]);

  const toggleTag = (name: string) => {
    const next = activeTags.includes(name)
      ? activeTags.filter((tg) => tg !== name)
      : [...activeTags, name];
    setSearch({ tag: next.length > 0 ? next.join(",") : undefined });
  };

  const counts = useMemo(() => {
    const out: Record<number, number> = {};
    for (const a of allArticlesQ.data ?? []) {
      out[a.category_id] = (out[a.category_id] ?? 0) + 1;
    }
    return out;
  }, [allArticlesQ.data]);

  const categoriesById = useMemo(
    () => new Map((categoriesQ.data ?? []).map((c) => [c.id, c])),
    [categoriesQ.data],
  );

  const stateFilterItems: SelectMenuItem<StateFilter>[] = STATE_FILTERS.map((s) => ({
    value: s,
    label: t(`kb.state.${s}`),
  }));

  const sidebarBody = categoriesQ.isLoading ? (
    <div className="flex justify-center py-6">
      <Spinner />
    </div>
  ) : (categoriesQ.data ?? []).length === 0 ? (
    <div className="space-y-2 px-2 py-3">
      <p className="text-xs text-muted">{t("kb.noCategories")}</p>
      <Button
        variant="secondary"
        size="sm"
        data-testid="kb-first-category"
        onClick={openCategoriesCreate}
      >
        {t("kb.createFirstCategory")}
      </Button>
    </div>
  ) : (
    <>
      <CategoryTree
        categories={categoriesQ.data ?? []}
        selectedId={categoryId}
        counts={counts}
        onSelect={(id) => {
          setSearch({ category_id: id ?? undefined });
          setDrawerOpen(false);
        }}
      />
      <button
        type="button"
        data-testid="kb-manage-categories"
        onClick={() => {
          setSearch({ tab: "categories" });
          setDrawerOpen(false);
        }}
        className="mt-2 block px-2 text-left text-xs text-muted hover:text-accent hover:underline"
      >
        {t("kb.manageCategories")}
      </button>
    </>
  );

  return (
    <div className="relative flex min-h-0 flex-1" data-testid="kb-page">
      {tab === "articles" && (
        <aside className="hidden w-56 shrink-0 overflow-y-auto border-r border-hairline bg-surface p-2 md:block lg:w-64">
          <div className="mb-2 flex items-center justify-between px-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
              {t("kb.sidebar")}
            </h2>
            <button
              type="button"
              data-testid="kb-new-category"
              title={t("kb.newCategory")}
              aria-label={t("kb.newCategory")}
              onClick={openCategoriesCreate}
              className="flex h-5 w-5 items-center justify-center rounded border border-hairline text-sm leading-none text-muted hover:border-accent hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
            >
              +
            </button>
          </div>
          {sidebarBody}
        </aside>
      )}

      {tab === "articles" && drawerOpen && (
        <div className="fixed inset-0 z-30 md:hidden">
          <button
            type="button"
            aria-label={t("common.back")}
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-64 overflow-y-auto border-r border-hairline bg-surface p-2 shadow-xl">
            <div className="mb-2 flex items-center justify-between px-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
                {t("kb.sidebar")}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setDrawerOpen(false)}>
                ✕
              </Button>
            </div>
            {sidebarBody}
          </div>
        </div>
      )}

      <div className="min-w-0 flex-1 space-y-3 p-3">
        <div className="flex flex-wrap items-center gap-2">
          {tab === "articles" && (
            <Button
              variant="secondary"
              size="sm"
              className="md:hidden"
              onClick={() => setDrawerOpen(true)}
              data-testid="kb-drawer-toggle"
            >
              {t("kb.sidebar")}
            </Button>
          )}
          <h1 className="font-display text-lg font-semibold text-ink">
            {t("kb.title")}
          </h1>
          <Tabs
            items={tabItems}
            value={tab}
            onChange={(id) => setSearch({ tab: id === "articles" ? undefined : (id as KbTab) })}
            className="ml-1"
          />
          {tab === "articles" && (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <SelectMenu
                items={stateFilterItems}
                value={state}
                onSelect={(v) => setSearch({ state: v })}
                panelTestId="kb-state-filter-panel"
                trigger={({ open, ref, toggleProps }) => (
                  <button
                    ref={ref}
                    type="button"
                    data-testid="kb-state-filter"
                    {...toggleProps}
                    className="flex min-w-[9rem] items-center justify-between gap-2 rounded-md border border-hairline bg-surface px-2 py-1.5 text-sm text-ink hover:bg-surface-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
                  >
                    <span>{t(`kb.state.${state}`)}</span>
                    <ChevronDownIcon
                      className={cn("text-muted transition-transform duration-150", open && "rotate-180")}
                    />
                  </button>
                )}
              />
              <Button
                variant="primary"
                size="sm"
                data-testid="kb-new-article"
                onClick={() => void navigate({ to: "/agent/kb/new" })}
              >
                {t("kb.newArticle")}
              </Button>
            </div>
          )}
        </div>

        {tab === "categories" ? (
          <KbCategoriesPanel initialCreate={!!search.new} />
        ) : (
        <>
        {(tagsQ.data ?? []).length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="kb-tag-bar">
            {tagBar.visible.map((tg) => {
              const active = activeTags.includes(tg.name);
              return (
                <button
                  key={tg.name}
                  type="button"
                  data-testid={`kb-tag-filter-${tg.name}`}
                  aria-pressed={active}
                  onClick={() => toggleTag(tg.name)}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[12px] font-medium transition-colors duration-100",
                    active
                      ? "border-accent bg-accent-dim text-accent"
                      : "border-hairline bg-surface text-muted hover:border-accent/50 hover:text-ink",
                  )}
                >
                  {tg.name}
                  <span className="text-[10px] tabular-nums opacity-70">{tg.article_count}</span>
                </button>
              );
            })}
            {tagBar.hasMore && (
              <button
                type="button"
                data-testid="kb-tag-bar-more"
                onClick={() => setTagsExpanded((v) => !v)}
                className="text-[12px] font-medium text-accent hover:underline"
              >
                {tagsExpanded ? t("kb.tagBarLess") : t("kb.tagBarMore")}
              </button>
            )}
          </div>
        )}

        {articlesQ.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (articlesQ.data ?? []).length === 0 ? (
          <div
            className="rounded-lg border border-dashed border-hairline bg-surface px-4 py-8 text-center"
            data-testid="kb-empty"
          >
            <p className="text-sm font-medium text-ink">{t("kb.empty")}</p>
            <div className="mt-3 flex justify-center gap-2">
              {(categoriesQ.data ?? []).length === 0 ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={openCategoriesCreate}
                >
                  {t("kb.createFirstCategory")}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void navigate({ to: "/agent/kb/new" })}
                >
                  {t("kb.newArticle")}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="kb-article-list">
            {(articlesQ.data ?? []).map((a) => (
              <li key={a.id}>
                <Link
                  to="/agent/kb/$articleId"
                  params={{ articleId: String(a.id) }}
                  data-testid={`kb-article-${a.id}`}
                  className="block rounded-lg border border-hairline bg-surface p-3 transition-colors duration-100 hover:border-accent/60 hover:bg-surface-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-muted">#{a.id}</span>
                    <Badge tone={STATE_TONE[a.state] ?? "muted"}>
                      {t(`kb.state.${a.state}`, a.state)}
                    </Badge>
                    <span className="text-xs uppercase text-muted">
                      {a.language}
                    </span>
                    {categoriesById.get(a.category_id) && (
                      <span className="text-xs text-muted">
                        {categoriesById.get(a.category_id)!.name}
                      </span>
                    )}
                    {(a.tags ?? []).map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center rounded-full bg-accent-dim px-2 py-0.5 text-[11px] font-medium text-accent"
                        data-testid={`kb-row-tag-${a.id}-${tag}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="mt-1 text-sm font-medium text-ink">{a.title}</p>
                  <p className="mt-0.5 text-xs text-muted">
                    {t("kb.lastChanged", {
                      date: formatDateTime(a.change_time, i18n.language),
                    })}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
        </>
        )}
      </div>
    </div>
  );
}
